import { APP_ROUTES } from "const";

import { lazy, useEffect } from "react";
import { ICustomRouteObject, IOutletAuth } from "types";
import {
  CreateNewPasswordForm,
  Dashboard,
  IssueList,
  LoginForm,
  NotFound,
  ProfileForm,
  ForgetPasswordForm,
  SignUpForm,
  SolutionList,
  ConfirmEmail,
  VerifyOtp,
  PublicLayoutError,
  Issue,
  Solution,
  MotivationalList,
  Motivational,
  Handouts,
  ActionGroup,
  Admin,
  LandingPage,
  Impact,
} from "modules";
import { NavigationShowcase, HeroLayoutsShowcase } from "mockups";
import { MainLayout } from "layouts";
import { Navigate, useOutletContext } from "react-router-dom";
import GroupList from "modules/GroupList";
import LandingPageOriginal from "modules/LandingPage/LandingPageRedesign";
import { MotivationalAddEdit } from "modules/MotivationalAddEdit";
import { Loader } from "components";
import { useAuth } from "layouts/layouts.hooks";

const PrivateLayout = lazy(() => import("../layouts/PrivateLayout"));
const PublicLayout = lazy(() => import("../layouts/PublicLayout"));
const CommonLayout = lazy(() => import("../layouts/CommonLayout"));
const BlankLayout = lazy(() => import("../layouts/BlankLayout"));

interface IRouteAccess {
  children: any;
  redirectTo: string;
}

function PublicAccess({ children, redirectTo = "#" }: IRouteAccess) {
  const authContext = useOutletContext<IOutletAuth>();
  useEffect(() => {
    authContext.setAccessType("public");
  }, [authContext]);

  return authContext?.isAuth ? <Navigate to={redirectTo} /> : children;
}

function PrivateAccess({ children, redirectTo = "#" }: IRouteAccess) {
  const authContext = useOutletContext<IOutletAuth>();
  useEffect(() => {
    authContext.setAccessType("private");
  }, [authContext]);
  return authContext?.isAuth ? children : <Navigate to={redirectTo} />;
}

function CommonAccess({ children }: IRouteAccess) {
  const authContext = useOutletContext<IOutletAuth>();
  useEffect(() => {
    authContext.setAccessType("common");
  }, [authContext]);

  return children;
}

// Standalone landing layout - bypasses MainLayout entirely for clean landing page presentation
function LandingLayout() {
  const { loading } = useAuth();

  if (loading) {
    return <Loader />;
  }

  // Show landing page for all users (authenticated and unauthenticated)
  return <LandingPage />;
}

// Standalone auth layout - bypasses MainLayout for clean auth page presentation
function AuthLayout({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loader />;
  }

  // If user is authenticated, redirect to issues
  if (user?.emailVerified) {
    return <Navigate to={APP_ROUTES.ISSUES} replace />;
  }

  // Show auth pages without any app shell
  return <>{children}</>;
}

const routes: ICustomRouteObject[] = [
  // Landing page routes - standalone, bypasses MainLayout for clean presentation
  {
    path: "/",
    element: <LandingLayout />,
  },
  {
    path: APP_ROUTES.LANDING,
    element: <LandingLayout />,
  },
  // Original full landing page — kept for reference
  {
    path: "/landing-original",
    element: <LandingPageOriginal />,
  },
  // Auth routes - standalone, bypasses MainLayout for clean auth presentation
  {
    path: APP_ROUTES.SIGN_IN,
    element: <AuthLayout><LoginForm /></AuthLayout>,
  },
  {
    path: APP_ROUTES.SIGN_UP,
    element: <AuthLayout><SignUpForm /></AuthLayout>,
  },
  {
    path: APP_ROUTES.VERIFY_OTP,
    element: <AuthLayout><VerifyOtp /></AuthLayout>,
  },
  {
    path: APP_ROUTES.FORGET_PASSWORD,
    element: <AuthLayout><ForgetPasswordForm /></AuthLayout>,
  },
  {
    path: APP_ROUTES.CHANGE_PASSWORD,
    element: <AuthLayout><CreateNewPasswordForm /></AuthLayout>,
  },
  // Main app routes - wrapped in MainLayout with header/footer
  {
    element: <MainLayout />,
    children: [
      {
        element: (
          <PrivateAccess redirectTo={APP_ROUTES.ISSUES}>
            <PrivateLayout />
          </PrivateAccess>
        ),
        children: [
          {
            path: APP_ROUTES.PROFILE + "/:userId",
            element: <ProfileForm />,
          },
          {
            path: APP_ROUTES.PROFILE,
            element: <ProfileForm />,
          },
          {
            path: APP_ROUTES.ADMIN,
            element: <Admin />,
          },
        ],
      },
      {
        element: (
          <CommonAccess redirectTo={APP_ROUTES.NOTFOUND}>
            <CommonLayout />
          </CommonAccess>
        ),
        children: [
          {
            path: APP_ROUTES.DASHBOARD,
            element: <Dashboard />,
          },
          {
            path: APP_ROUTES.IMPACT,
            element: <Impact />,
          },
          {
            path: APP_ROUTES.ISSUES,
            element: <IssueList />,
          },
          {
            path: APP_ROUTES.HANDOUTS,
            element: <Handouts />,
          },
          {
            path: APP_ROUTES.MOTIVATIONAL_LIST,
            element: <MotivationalList />,
          },

          {
            path: APP_ROUTES.MOTIVATIONAL_CREATE,
            element: <MotivationalAddEdit />,
          },
          {
            path: APP_ROUTES.MOTIVATIONAL_EDIT + "/:id",
            element: <MotivationalAddEdit />,
          },
          {
            path: APP_ROUTES.MOTIVATIONAL + "/:id",
            element: <Motivational />,
          },
          {
            path: APP_ROUTES.ISSUE + "/:id",
            element: <Issue />,
          },
          {
            path: APP_ROUTES.SOLUTION + "/:id",
            element: <Solution />,
          },
          {
            path: APP_ROUTES.ACTION_GROUP + "/:id",
            element: <ActionGroup />,
          },
          {
            path: APP_ROUTES.SOLUTION_LIST,
            element: <SolutionList />,
          },
          {
            path: APP_ROUTES.ACTION_GROUP_LIST,
            element: <GroupList />,
          },
          {
            path: APP_ROUTES.CONFIRM_EMAIL,
            element: <ConfirmEmail />,
          },
          {
            path: APP_ROUTES.MOCK_NAVIGATION,
            element: <NavigationShowcase />,
          },
          {
            path: APP_ROUTES.MOCK_HERO,
            element: <HeroLayoutsShowcase />,
          },
        ],
      },
      {
        element: (
          <CommonAccess redirectTo={APP_ROUTES.NOTFOUND}>
            <BlankLayout />
          </CommonAccess>
        ),
        children: [{ path: "*", element: <NotFound /> }],
      },
      {
        element: (
          <PublicAccess redirectTo={APP_ROUTES.ISSUES}>
            <PublicLayout />
          </PublicAccess>
        ),
        children: [
          {
            path: APP_ROUTES.SIGN_IN,
            element: <LoginForm />,
          },
          {
            path: APP_ROUTES.SIGN_UP,
            element: <SignUpForm />,
          },
          {
            path: APP_ROUTES.CHANGE_PASSWORD,
            element: <CreateNewPasswordForm />,
          },
          {
            path: APP_ROUTES.FORGET_PASSWORD,
            element: <ForgetPasswordForm />,
          },
          {
            path: APP_ROUTES.ERROR,
            element: <PublicLayoutError />,
          },
        ],
      },
    ],
  },
];

export default routes;
